import React, { forwardRef } from 'react';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { styles } from './Select.styles';
import theme from '@src/styles/theme';
import IconButton from '@src/components/ui/IconButton';
import Spinner from '@src/components/ui/Spinner';
import { X as Close, ChevronDown } from '@src/components/ui/Icon/outline';
import Tag from '@src/components/ui/Tag';
import Card from '@src/components/ui/Card';

import type { SelectProps } from './types';

const LoadingIndicator = (props) => (
  <Spinner {...props} theme={theme} contrast />
);

const ClearIndicator = ({ cx, ...props }) => (
  <IconButton
    {...props}
    onClick={props.clearValue}
    theme={theme}
    color="neutral"
    rounded
    compact
    variant="ghosted"
  >
    <Close />
  </IconButton>
);

const DropdownIndicator = ({ cx, ...props }) => (
  <IconButton
    {...props}
    theme={theme}
    color="neutral"
    rounded
    compact
    variant="ghosted"
  >
    <ChevronDown />
  </IconButton>
);

const MultiValue = ({ cx, ...props }) => (
  <Tag
    {...props}
    compact
    color="light"
    label={props.children}
    onClick={() => {
      const value = Array.isArray(props.selectProps.value)
        ? props.selectProps.value
        : [props.selectProps.value].filter(Boolean);
      props.setValue(value.filter((option) => option !== props.data));
    }}
    endIcon={<Close />}
    theme={theme}
    style={{
      marginRight: 4,
    }}
  />
);

const Menu = ({ innerProps, children }) => (
  <Card {...innerProps} theme={theme} depth="sm" verticalPadding="2">
    {children}
  </Card>
);

const selectTheme = (reactSelectTheme) => ({
  ...reactSelectTheme,
  borderRadius: 0,
  colors: {
    primary: theme.colors.primary[500],
    primary75: theme.colors.primary[300],
    primary50: theme.colors.primary[100],
    primary25: theme.colors.primary[50],
    danger: theme.colors.danger[500],
    dangerLight: theme.colors.danger[200],
    neutral0: theme.colors.neutral[50],
    neutral5: theme.colors.neutral[50],
    neutral10: theme.colors.neutral[100],
    neutral20: theme.colors.neutral[200],
    neutral30: theme.colors.neutral[300],
    neutral40: theme.colors.neutral[400],
    neutral50: theme.colors.neutral[500],
    neutral60: theme.colors.neutral[600],
    neutral70: theme.colors.neutral[700],
    neutral80: theme.colors.neutral[800],
    neutral90: theme.colors.neutral[900],
  },
});

const SelectComponent = forwardRef(
  ({ isCreateable, ...otherProps }: SelectProps, ref) =>
    isCreateable ? (
      <CreatableSelect ref={ref as any} {...otherProps} />
    ) : (
      <ReactSelect ref={ref as any} {...otherProps} />
    )
);

const Select = (
  { customStyles, components, error, id, ...otherProps }: SelectProps,
  ref
) => (
  <SelectComponent
    ref={ref}
    inputId={id}
    {...otherProps}
    styles={{ ...styles({ error }), ...customStyles }}
    components={{
      ...components,
      LoadingIndicator,
      ClearIndicator,
      DropdownIndicator,
      MultiValue,
      Menu,
    }}
    theme={selectTheme}
    menuPortalTarget={document.body}
    menuPlacement="bottom"
  />
);
export default forwardRef(Select);
